import axios from "axios";
import moment from "moment";
import { createStore } from "zustand/vanilla";
import { useEffect, useState } from "react";
import { getCookie } from "../../../../utils/cookieUtils";
import { errorNoti } from "../../../../base/BasicNotifications";
import {
  ACCESS_TOKEN_COOKIE_KEY,
  ADMIN_ACCESS_TOKEN_COOKIE_KEY,
  ADMINS_COACH_ID_COOKIE_KEY,
} from "../../../../data/enums/TokenEnums";
import { strategyCanvasUrls } from "./StrategyCanvasUrls";

const nodeInstance = axios.create({
  baseURL: process.env.REACT_APP_NEST_BASE_URL,
});

const STEP_DATE_FORMAT = "Do MMM, YY";

// Saves a blob response as a normal file download via a throwaway <a> tag.
const triggerFileDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// The download endpoints return `409 conflict` as JSON, but since the
// request is made with responseType: "blob", axios hands that error body
// back as a Blob too - read it as text and parse it to get the real message.
const parseBlobErrorMessage = async (error, fallback) => {
  const data = error.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return parsed.message || fallback;
    } catch (_) {
      return fallback;
    }
  }
  return error.response?.data?.message || fallback;
};

const getInitials = (companyName = "") =>
  companyName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("");

// Maps one dashboard `clients[]` entry from the API to the props consumed by
// the SME card / steps components.
const mapDashboardClient = (client) => ({
  id: client.company_id,
  briefId: client.canvas_unique_id || null,
  initials: getInitials(client.company_name),
  name: client.company_name,
  ceo: `CEO: ${client.ceo_name}`,
  stage: client.stage,
  filled: client.stage_index,
  statusText: client.stage_label,
  statusSubText:
    client.blocks_committed !== null && client.blocks_committed !== undefined
      ? `${client.blocks_committed} out of ${client.total_blocks} blocks`
      : undefined,
  briefDisabled: !client.button?.enabled || !client.canvas_unique_id,
  buttonText: client.button?.label || "View brief",
  stepDates: (client.timestamps || []).map((timestamp) =>
    timestamp.at ? moment(timestamp.at).format(STEP_DATE_FORMAT) : "-",
  ),
});

export const useStrategyCanvasStore = createStore((set, get) => ({
  dashboardLoading: false,
  clients: null,

  // Brief tab data, keyed by canvas uniqueId so switching tabs (or between
  // briefs) never refetches data already loaded in this session.
  summaryByUniqueId: {},
  businessContextByUniqueId: {},
  documentsByUniqueId: {},
  howCeoBuiltByUniqueId: {},
  xrayByUniqueId: {},
  summaryLoading: false,
  businessContextLoading: false,
  documentsLoading: false,
  howCeoBuiltLoading: false,
  xrayLoading: false,
  summaryDownloading: false,
  howCeoBuiltDownloading: false,
  canvasDownloading: false,
  downloadingFileIds: {},

  // Lean status/state check (strategy-canvas-be-changes-fe-integration.md
  // section 3), keyed by uniqueId same as everything else on this store.
  canvasStatusByUniqueId: {},
  canvasStatusLoading: false,

  _resolveAuthHeader: () => {
    const accessToken = getCookie(ACCESS_TOKEN_COOKIE_KEY);
    const adminAccessToken = getCookie(ADMIN_ACCESS_TOKEN_COOKIE_KEY);
    const adminsCoachId = getCookie(ADMINS_COACH_ID_COOKIE_KEY);
    if (accessToken) return `Bearer ${accessToken}`;
    if (adminAccessToken && adminsCoachId) return `Bearer ${adminAccessToken}`;
    return null;
  },

  _get: async (url) => {
    const authHeader = get()._resolveAuthHeader();
    return nodeInstance.get(url, {
      headers: authHeader ? { Authorization: authHeader } : {},
    });
  },

  _post: async (url, body = {}) => {
    const authHeader = get()._resolveAuthHeader();
    return nodeInstance.post(url, body, {
      headers: authHeader ? { Authorization: authHeader } : {},
    });
  },

  _getBlob: async (url) => {
    const authHeader = get()._resolveAuthHeader();
    return nodeInstance.get(url, {
      headers: authHeader ? { Authorization: authHeader } : {},
      responseType: "blob",
    });
  },

  fetchDashboard: async () => {
    set({ dashboardLoading: true });
    try {
      const response = await get()._get(strategyCanvasUrls.GET_DASHBOARD);
      const clients = (response.data?.data?.clients || []).map(
        mapDashboardClient,
      );
      set({ clients, dashboardLoading: false });
    } catch (error) {
      errorNoti(
        error.response?.data?.message || "Couldn't load the dashboard",
      );
      set({ clients: [], dashboardLoading: false });
    }
  },

  fetchCanvasStatus: async (uniqueId) => {
    if (!uniqueId) return;
    set({ canvasStatusLoading: true });
    try {
      const response = await get()._get(
        strategyCanvasUrls.GET_CANVAS_STATUS.replace("{uniqueId}", uniqueId),
      );
      set((state) => ({
        canvasStatusByUniqueId: {
          ...state.canvasStatusByUniqueId,
          [uniqueId]: response.data?.data || null,
        },
        canvasStatusLoading: false,
      }));
    } catch (error) {
      errorNoti(error.response?.data?.message || "Couldn't load the canvas status");
      set((state) => ({
        canvasStatusByUniqueId: { ...state.canvasStatusByUniqueId, [uniqueId]: null },
        canvasStatusLoading: false,
      }));
    }
  },

  fetchSummary: async (uniqueId) => {
    if (!uniqueId || get().summaryByUniqueId[uniqueId]) return;
    set({ summaryLoading: true });
    try {
      const response = await get()._get(
        strategyCanvasUrls.GET_SUMMARY.replace("{uniqueId}", uniqueId),
      );
      set((state) => ({
        summaryByUniqueId: {
          ...state.summaryByUniqueId,
          [uniqueId]: response.data?.data || { story: null },
        },
        summaryLoading: false,
      }));
    } catch (error) {
      errorNoti(error.response?.data?.message || "Couldn't load the summary");
      set({ summaryLoading: false });
    }
  },

  fetchBusinessContext: async (uniqueId) => {
    if (!uniqueId || get().businessContextByUniqueId[uniqueId]) return;
    set({ businessContextLoading: true });
    try {
      const response = await get()._get(
        strategyCanvasUrls.GET_BUSINESS_CONTEXT.replace("{uniqueId}", uniqueId),
      );
      set((state) => ({
        businessContextByUniqueId: {
          ...state.businessContextByUniqueId,
          [uniqueId]: response.data?.data || { layers: [] },
        },
        businessContextLoading: false,
      }));
    } catch (error) {
      errorNoti(error.response?.data?.message || "Couldn't load the business context");
      set({ businessContextLoading: false });
    }
  },

  fetchDocuments: async (uniqueId) => {
    if (!uniqueId || get().documentsByUniqueId[uniqueId]) return;
    set({ documentsLoading: true });
    try {
      const response = await get()._get(
        strategyCanvasUrls.GET_DOCUMENTS.replace("{uniqueId}", uniqueId),
      );
      set((state) => ({
        documentsByUniqueId: {
          ...state.documentsByUniqueId,
          [uniqueId]: response.data?.data || { blocks: {} },
        },
        documentsLoading: false,
      }));
    } catch (error) {
      errorNoti(error.response?.data?.message || "Couldn't load the documents");
      set({ documentsLoading: false });
    }
  },

  // "How CEO built final canvas" tab - this endpoint isn't documented in
  // coach-strategy-canvas-fe-integration.md (it's explicitly listed under
  // "not yet available"). This call follows the same URL convention as the
  // other 3 tabs but fails silently (no error toast, no thrown state) so the
  // tab just keeps showing its empty state until the backend actually ships
  // the route - the moment it responds with data, this renders it.
  fetchHowCeoBuilt: async (uniqueId) => {
    if (!uniqueId || get().howCeoBuiltByUniqueId[uniqueId]) return;
    set({ howCeoBuiltLoading: true });
    try {
      const response = await get()._get(
        strategyCanvasUrls.GET_HOW_CEO_BUILT.replace("{uniqueId}", uniqueId),
      );
      set((state) => ({
        howCeoBuiltByUniqueId: {
          ...state.howCeoBuiltByUniqueId,
          [uniqueId]: response.data?.data || null,
        },
        howCeoBuiltLoading: false,
      }));
    } catch (error) {
      // Silent on purpose - endpoint isn't confirmed to exist yet.
      set((state) => ({
        howCeoBuiltByUniqueId: {
          ...state.howCeoBuiltByUniqueId,
          [uniqueId]: null,
        },
        howCeoBuiltLoading: false,
      }));
    }
  },

  // X-Ray Report tab - returns the survey_id used to build the X-Ray score
  // navigation link (the icon inside the tab opens
  // /coach-panel/x-ray/score/{survey_id} in a new tab).
  fetchXray: async (uniqueId) => {
    if (!uniqueId || get().xrayByUniqueId[uniqueId]) return;
    set({ xrayLoading: true });
    try {
      const response = await get()._get(
        strategyCanvasUrls.GET_XRAY.replace("{uniqueId}", uniqueId),
      );
      set((state) => ({
        xrayByUniqueId: {
          ...state.xrayByUniqueId,
          [uniqueId]: response.data?.data || null,
        },
        xrayLoading: false,
      }));
    } catch (error) {
      errorNoti(error.response?.data?.message || "Couldn't load the X-Ray Report");
      set({ xrayLoading: false });
    }
  },

  // Review Mode - section 5.1. Called once when the coach clicks "Begin
  // canvas review" on the brief page, before navigating to the Review
  // screen. Idempotent on the backend (safe to call every time), and
  // failure here shouldn't block navigation - it only flips the dashboard
  // button label, the review screen itself (5.2, not yet wired) is what the
  // coach actually needs to see next.
  enterReview: async (uniqueId) => {
    if (!uniqueId) return;
    try {
      await get()._post(
        strategyCanvasUrls.POST_REVIEW_ENTER.replace("{uniqueId}", uniqueId),
      );
    } catch (error) {
      errorNoti(error.response?.data?.message || "Couldn't open review mode");
    }
  },

  // Section 4.1 - "Download PDF" on the Summary tab. Generated on demand,
  // not stored - backend returns 409 conflict if there's no story yet.
  downloadSummaryPdf: async (uniqueId) => {
    if (!uniqueId || get().summaryDownloading) return;
    set({ summaryDownloading: true });
    try {
      const response = await get()._getBlob(
        strategyCanvasUrls.GET_SUMMARY_DOWNLOAD.replace("{uniqueId}", uniqueId),
      );
      triggerFileDownload(
        new Blob([response.data], { type: "application/pdf" }),
        "business-summary.pdf",
      );
    } catch (error) {
      errorNoti(
        await parseBlobErrorMessage(error, "Couldn't download the summary PDF"),
      );
    } finally {
      set({ summaryDownloading: false });
    }
  },

  // Section 4.3 - "Download PDF" on the "How CEO built final canvas" tab.
  // Same on-demand pattern; 409 conflict while the narrative is still a
  // placeholder (is_placeholder: true).
  downloadCeoJourneyPdf: async (uniqueId) => {
    if (!uniqueId || get().howCeoBuiltDownloading) return;
    set({ howCeoBuiltDownloading: true });
    try {
      const response = await get()._getBlob(
        strategyCanvasUrls.GET_CEO_JOURNEY_DOWNLOAD.replace("{uniqueId}", uniqueId),
      );
      triggerFileDownload(
        new Blob([response.data], { type: "application/pdf" }),
        "how-ceo-built-final-canvas.pdf",
      );
    } catch (error) {
      errorNoti(
        await parseBlobErrorMessage(error, "Couldn't download the CEO journey PDF"),
      );
    } finally {
      set({ howCeoBuiltDownloading: false });
    }
  },

  // Coach's canvas download button (Strategy Canvas view, shown once the
  // canvas is LOCKED) - same on-demand blob-download pattern as the summary/
  // CEO-journey PDFs.
  downloadCanvasPdf: async (uniqueId) => {
    if (!uniqueId || get().canvasDownloading) return;
    set({ canvasDownloading: true });
    try {
      const response = await get()._getBlob(
        strategyCanvasUrls.GET_CANVAS_DOWNLOAD.replace("{uniqueId}", uniqueId),
      );
      triggerFileDownload(
        new Blob([response.data], { type: "application/pdf" }),
        "strategy-canvas.pdf",
      );
    } catch (error) {
      errorNoti(await parseBlobErrorMessage(error, "Couldn't download the canvas PDF"));
    } finally {
      set({ canvasDownloading: false });
    }
  },

  // Documents tab - "To download a file, call GET /shared/download/coach/:file_id"
  // (section 4.2 field notes). Existing, already-deployed shared endpoint -
  // streams the raw file back with its own Content-Type.
  downloadSharedFile: async (fileId, filename) => {
    if (!fileId || get().downloadingFileIds[fileId]) return;
    set((state) => ({
      downloadingFileIds: { ...state.downloadingFileIds, [fileId]: true },
    }));
    try {
      const response = await get()._getBlob(
        strategyCanvasUrls.GET_SHARED_FILE_DOWNLOAD.replace("{fileId}", fileId),
      );
      const contentType = response.headers?.["content-type"] || "application/octet-stream";
      triggerFileDownload(
        new Blob([response.data], { type: contentType }),
        filename || "download",
      );
    } catch (error) {
      errorNoti(await parseBlobErrorMessage(error, "Couldn't download the file"));
    } finally {
      set((state) => ({
        downloadingFileIds: { ...state.downloadingFileIds, [fileId]: false },
      }));
    }
  },
}));

export default useStrategyCanvasStore;

// React hook wrapper to subscribe to the vanilla store and trigger re-renders
export const useStrategyCanvasStoreSelector = (selector) => {
  const [selected, setSelected] = useState(() =>
    selector(useStrategyCanvasStore.getState()),
  );
  useEffect(() => {
    const unsub = useStrategyCanvasStore.subscribe((state) => {
      setSelected(() => selector(state));
    });
    return () => unsub();
  }, [selector]);
  return selected;
};
